/**
 * Match-lore aggregation — the proprietary emotional head-to-head, computed as
 * a pure fold over `coyg.lore.v1` entries grouped by opponent (spec §6, §1.3).
 *
 * Every output is DERIVED-ON-READ from the lore entries, so the H2H can never
 * drift from them — it *is* them, summed. Three honesty laws run through all of
 * it (spec §value 3, §6.4):
 *
 *   1. `illustrative: true` demo entries are excluded from EVERY aggregate.
 *   2. No fabrication: n===0 → "no data yet" (null); never a fake 50%.
 *   3. A single match is never a trend: 1≤n<3 renders a raw fraction, never a
 *      bare percent. The reaction curve is null when there are no events
 *      (manual mode — provider-off).
 */
import type {
  H2HInsight,
  H2HMetric,
  LoreEntry,
  LoreMoodSample,
  Mood,
} from './types';

const MOODS: Mood[] = ['buzzing', 'relieved', 'anxious', 'frustrated'];

/** The set L_opp: non-illustrative lore entries for one opponent (spec §6). */
function entriesFor(entries: LoreEntry[], opponent: string): LoreEntry[] {
  return entries.filter((e) => !e.illustrative && e.opponent === opponent);
}

/**
 * The universal honesty gate (spec §6.4). Wraps a percentage in its sample-size
 * caveat so a single match can never render as a trend.
 *   n === 0     → { caveat: 'none' } (no data yet)
 *   1 ≤ n < 3   → { caveat: 'raw', fraction } (show "k of n", never a bare %)
 *   n ≥ 3       → { caveat: 'ok' } (the percent is allowed, n still shown)
 */
export function withSampleCaveat(value: number | null, n: number, fraction?: string): H2HMetric {
  if (n === 0 || value === null) return { value: n === 0 ? null : value, n, caveat: 'none' };
  if (n < 3) return { value, n, caveat: 'raw', fraction };
  return { value, n, caveat: 'ok' };
}

/**
 * Prediction calibration (spec §6.1): how often the North Bank called a meeting
 * right, over entries that carry a non-null `called`.
 */
export function calibrationFor(entries: LoreEntry[], opponent: string): H2HMetric {
  const called = entriesFor(entries, opponent).filter((e) => e.prediction.called !== null);
  const correct = called.filter((e) => e.prediction.correct === true).length;
  if (called.length === 0) return { value: null, n: 0, caveat: 'none' };
  const pct = Math.round((100 * correct) / called.length);
  return withSampleCaveat(pct, called.length, `${correct} of ${called.length}`);
}

interface MoodVerdict {
  mood: Mood | null;
  share: number | null;
  n: number;
}

/**
 * Mood signature for one venue (spec §6.2): the dominant final mood across
 * L_opp, weighted by each entry's `moodArc.final.n`. Suppressed (null) when no
 * entry played at that venue.
 */
function moodSignatureForVenue(entries: LoreEntry[], venue: 'H' | 'A'): MoodVerdict {
  const at = entries.filter((e) => e.venue === venue);
  if (at.length === 0) return { mood: null, share: null, n: 0 };
  // Accumulate mood mass = (mood % share) × (sample size) so a 1,200-fan FT
  // counts more than a 40-fan one. The final mood split lives on the `ft`
  // sample (`final` carries only the scalar dominant/avg/n per the contract);
  // weight it by `final.n`.
  const acc: Record<Mood, number> = { buzzing: 0, relieved: 0, anxious: 0, frustrated: 0 };
  let totalMass = 0;
  for (const e of at) {
    const n = e.moodArc.final.n;
    const ft = e.moodArc.samples.find((s) => s.t === 'ft');
    const moods = ft?.moods;
    if (!moods) continue;
    for (const m of MOODS) {
      const mass = ((moods[m] ?? 0) / 100) * n;
      acc[m] += mass;
      totalMass += mass;
    }
  }
  if (totalMass === 0) return { mood: null, share: null, n: at.length };
  let best: Mood = MOODS[0]!;
  for (const m of MOODS) if (acc[m] > acc[best]) best = m;
  return { mood: best, share: Math.round((100 * acc[best]) / totalMass), n: at.length };
}

export function moodSignature(
  entries: LoreEntry[],
  opponent: string,
): { home: MoodVerdict; away: MoodVerdict } {
  const opp = entriesFor(entries, opponent);
  return { home: moodSignatureForVenue(opp, 'H'), away: moodSignatureForVenue(opp, 'A') };
}

/** The phase whose mood sample we read after a concession (spec §6.3). */
function sampleAfterConcession(samples: LoreMoodSample[], concededFirstHalf: boolean): LoreMoodSample | null {
  const want: LoreMoodSample['t'] = concededFirstHalf ? 'ht' : 'ft';
  return samples.find((s) => s.t === want) ?? samples.find((s) => s.t === 'ft') ?? null;
}

const dominantMood = (s: LoreMoodSample | null): Mood | null => {
  if (!s) return null;
  let best: Mood = MOODS[0]!;
  for (const m of MOODS) if ((s.moods[m] ?? 0) > (s.moods[best] ?? 0)) best = m;
  return s.moods[best] && s.moods[best]! > 0 ? best : null;
};

/**
 * Reaction curve (spec §6.3): mood + recovery after conceding the first goal.
 * Provider-on only — needs `result.events` to know who scored first. In manual
 * mode the events array is empty, so this is honesty-gated OFF (caveat 'off').
 */
export function reactionCurve(
  entries: LoreEntry[],
  opponent: string,
): H2HInsight['reaction'] {
  const opp = entriesFor(entries, opponent);
  // No events anywhere ⇒ provider is off; the metric has no input.
  const hasEvents = opp.some((e) => e.result.events.length > 0);
  if (!hasEvents) {
    return { metric: { value: null, n: 0, caveat: 'off' }, mood: null, recovered: 0, conceded: 0 };
  }
  // The first goal-type event of the match (goal/pen/own-goal).
  const isGoal = (t: string) => t === 'goal' || t === 'pen' || t === 'own-goal';
  const concededFirst = opp.filter((e) => {
    const first = [...e.result.events].sort((a, b) => a.min - b.min).find((ev) => isGoal(ev.type));
    if (!first) return false;
    // An own-goal flips the scorer: our own-goal benefits them, theirs benefits us.
    const theyScored = first.type === 'own-goal' ? first.ours === true : first.ours === false;
    return theyScored;
  });
  if (concededFirst.length === 0) {
    return { metric: { value: null, n: 0, caveat: 'none' }, mood: null, recovered: 0, conceded: 0 };
  }
  // Dominant mood at the phase sample after the (first) concession, and the
  // recovery rate (came back to a draw-or-better).
  const moodTally: Record<Mood, number> = { buzzing: 0, relieved: 0, anxious: 0, frustrated: 0 };
  let recovered = 0;
  for (const e of concededFirst) {
    const first = [...e.result.events].sort((a, b) => a.min - b.min).find((ev) => isGoal(ev.type))!;
    const dom = dominantMood(sampleAfterConcession(e.moodArc.samples, first.min <= 45));
    if (dom) moodTally[dom] += 1;
    if (e.result.r === 'W' || e.result.r === 'D') recovered += 1;
  }
  let mood: Mood | null = null;
  for (const m of MOODS) if (moodTally[m] > (mood ? moodTally[mood] : 0)) mood = m;
  const pct = Math.round((100 * recovered) / concededFirst.length);
  const metric = withSampleCaveat(pct, concededFirst.length, `${recovered} of ${concededFirst.length}`);
  return { metric, mood, recovered, conceded: concededFirst.length };
}

/**
 * The full emotional H2H for one opponent — a pure fold over the loaded lore
 * (spec §1.3, "within available lore"). `n` always travels so the window is
 * never misrepresented as all-time.
 */
export function aggregateH2H(entries: LoreEntry[], opponent: string): H2HInsight {
  const opp = entriesFor(entries, opponent);
  const record = { w: 0, d: 0, l: 0 };
  for (const e of opp) {
    if (e.result.r === 'W') record.w += 1;
    else if (e.result.r === 'D') record.d += 1;
    else record.l += 1;
  }
  const sig = moodSignature(entries, opponent);
  return {
    opponent,
    n: opp.length,
    record,
    calibration: calibrationFor(entries, opponent),
    mood: { home: sig.home, away: sig.away },
    reaction: reactionCurve(entries, opponent),
  };
}
